import { observable, runInAction } from 'mobx'
import { CommonContainer } from 'src/models/Common'
import {
  customerContainer,
  accountsContainer,
  storeContainer,
  cartContainer,
} from 'src/models'
import { APPConfig, convertToBoolean, setLocalStorage } from 'config/appConfig'
import {
  IS_BROWSER,
  i18nTranslate,
  jitsuSignUpRedirect,
  getLocalStorage,
  getLocaleCodeFromUrl,
  trackErrorInInstana,
  restrictOtherMarketUser,
} from 'src/utils'
import { pageNames } from 'src/routes/pathParams'
import { PUBSUB, subscribe, unSubscribeEvent } from 'src/pubsub'
import { toastState } from 'src/views/components/Toast/ToastState'
import { isEquinoxPOSite } from 'src/utils/mySiteUtils'
import {
  ACCOUNT_TYPE_MEMBER,
  ACCOUNT_TYPE_BRAND_AFFILIATE,
  RE_ENROLL_TYPE_MEMBER,
  RE_ENROLL_TYPE_BRAND_AFFILIATE,
} from 'src/utils/constant'

class GlobalModalsContainer extends CommonContainer {
  @observable isToShowNotificationPopup = false
  @observable isToShowNotificationErrorPopup = false
  @observable isToShowPhoneVerification = true
  @observable showWarningPopup = false
  @observable dcsvStatus = false
  @observable dcsvResponseValue = false
  @observable isPhoneNumberVerified = false
  @observable isFromFourMonthNotification = false
  @observable verifyPhoneBasedOnReEnroll = false
  @observable invitationType = ''
  @observable isReEnrollAsMember = RE_ENROLL_TYPE_MEMBER
  @observable isReEnrollASBrandAffiliate = RE_ENROLL_TYPE_BRAND_AFFILIATE
  @observable phoneVerifyFromSession = false
  @observable isToShowReEnrollPop = false
  @observable termDate = ''
  @observable isFromFourMonthCall = false
  @observable isToShowReEnrollSignOutErrorPopup =
    getLocalStorage('isToShowReEnrollSignOutErrorPopup') || false
  @observable enableDsSearchPopup = false
  @observable showRegionRestrictionModal = false

  isCheckDcsvCalled = false
  modalIgnored = false
  hasCheckedOnProfileLoad = false
  directSellerPOToken = null
  nonHomeMarket = null

  initSubscriptions = () => {
    if (isEquinoxPOSite()) {
      this.directSellerPOToken = subscribe(
        PUBSUB.DIRECT_SELLER_PRODUCT,
        (event, data) => {
          const { productData = {} } = data
          cartContainer?.setSkuDetailsForNotifyDirectSeller(productData)
          cartContainer?.toggleNotifyDirectSellerPopup()
        }
      )
      this.nonHomeMarket = subscribe(PUBSUB.NON_HOME_MARKET, () => {
        toastState.setToastMessage(
          i18nTranslate(
            'cart.homeMarket',
            'Please switch to your home market to make a purchase.'
          )
        )
      })
    }
  }

  destroySubscriptions = () => {
    unSubscribeEvent(this.directSellerPOToken)
    unSubscribeEvent(this.nonHomeMarket)
  }

  checkAndShowRegionRestrictionModal = currentPageUrl => {
    if (!this.modalIgnored) {
      try {
        this.showRegionRestrictionModal = restrictOtherMarketUser(
          currentPageUrl,
          customerContainer
        )
      } catch (error) {
        trackErrorInInstana({
          errorReport: 'global_modals_region_restriction_check_error',
          errorData: { errorMessage: error?.message || String(error) },
        })
      }
    }
  }

  handleCloseRegionRestrictionModal = () => {
    this.modalIgnored = true
    this.showRegionRestrictionModal = false
  }

  checkDsSearchQueryParam = () => {
    if (!IS_BROWSER) return
    try {
      const searchParams = new URLSearchParams(window.location.search)
      const param = searchParams.get('dsSearch')
      if (param === 'true' || param === '1') {
        runInAction(() => {
          this.enableDsSearchPopup = true
        })
      }
    } catch (error) {
      trackErrorInInstana({
        errorReport: 'global_modals_ds_search_query_param_error',
        errorData: { errorMessage: error?.message || String(error) },
      })
    }
  }

  handleOpenDsSearchPopup = () => {
    runInAction(() => {
      this.enableDsSearchPopup = true
    })
    if (IS_BROWSER) {
      try {
        const searchParams = new URLSearchParams(window.location.search)
        if (!searchParams.has('dsSearch')) {
          searchParams.set('dsSearch', 'true')
          const newUrl = `${window.location.pathname}?${searchParams.toString()}`
          window.history.pushState({}, '', newUrl)
        }
      } catch (error) {
        trackErrorInInstana({
          errorReport: 'global_modals_ds_search_add_param_error',
          errorData: { errorMessage: error?.message || String(error) },
        })
      }
    }
  }

  handleCloseDsSearchPopup = () => {
    runInAction(() => {
      this.enableDsSearchPopup = false
    })
    if (IS_BROWSER) {
      try {
        const searchParams = new URLSearchParams(window.location.search)
        if (searchParams.has('dsSearch')) {
          searchParams.delete('dsSearch')
          const newUrl = searchParams.toString()
            ? `${window.location.pathname}?${searchParams.toString()}`
            : window.location.pathname
          window.history.replaceState({}, '', newUrl)
        }
      } catch (error) {
        trackErrorInInstana({
          errorReport: 'global_modals_ds_search_remove_param_error',
          errorData: { errorMessage: error?.message || String(error) },
        })
      }
    }
  }

  checkDcsvAndCommissionStatus = async () => {
    if (!customerContainer?.isRegisterUser) {
      return false
    }

    this.isCheckDcsvCalled = true
    const customerData = customerContainer?.profileResponse
    this.isPhoneNumberVerified = convertToBoolean(
      customerData?.customProperties?.isPhoneNumberVerified || false
    )
    const enableFourMonthNotification = convertToBoolean(
      APPConfig?.getAppConfig()?.enableFourMonthNotification
    )
    const accountType = customerData?.accounts?.accountType || ''
    const accountId = customerData?.accounts?.referenceAttribute || ''
    const accountStatus = customerData?.accounts?.status || ''
    let homeCountry = storeContainer.currentRegion || ''
    customerData?.accounts?.properties?.forEach((element = {}) => {
      if (element.attributeId === 'countryCode') {
        homeCountry = element?.value
      }
    })
    if (homeCountry === 'CA') {
      homeCountry = 'Canada'
    }

    if (
      enableFourMonthNotification &&
      customerContainer.isBrandAffiliate(accountType) &&
      homeCountry === storeContainer.storeIDValue &&
      customerContainer.isAccountTerminated != true &&
      accountStatus?.toLowerCase() === 'active'
    ) {
      const response = await accountsContainer.getDcsvStatus(accountId)
      const dcsvValue = convertToBoolean(response?.dcsvterm || false)
      this.termDate = response?.termdate

      if (
        accountsContainer.isSuccessResponse(response) &&
        dcsvValue &&
        this.isPhoneNumberVerified
      ) {
        this.dcsvResponseValue = dcsvValue
        this.dcsvStatus = true
      } else {
        this.dcsvResponseValue = dcsvValue
        this.dcsvStatus = false
        this.isFromFourMonthCall = true
      }
      await accountsContainer?.getCommissionCall()
    }
    accountsContainer.checkAccountStatus(accountsContainer, true)
  }

  showPhoneVerificationPopup = () => {
    this.isToShowPhoneVerification = true
  }

  handleCloseNotificationPopup = () => {
    this.dcsvStatus = false
  }

  handleOpenNotificationPopup = history => {
    this.isPhoneNumberVerified = convertToBoolean(
      customerContainer?.profileResponse?.customProperties
        ?.isPhoneNumberVerified
    )
    if (this.isPhoneNumberVerified && !this.isFromFourMonthNotification) {
      this.dcsvStatus = true
    } else if (this.dcsvResponseValue && !this.isPhoneNumberVerified) {
      this.dcsvStatus = true
    } else if (this.dcsvResponseValue && this.isPhoneNumberVerified) {
      this.showWarningPopup = false
      this.dcsvStatus = false
      accountsContainer.selectedAccountType = ACCOUNT_TYPE_MEMBER
      history.push(`${pageNames.b2bAccountProfile}`, { state: true })
    }
  }

  handleShowWarningPopupModal = () => {
    this.getMessage()
    const isAccountHold = accountsContainer.isAccountHold
    const isAccountIsBE = accountsContainer.isAccountIsBE
    const checkNumberOfBuyers = accountsContainer.checkNumberOfBuyers
    const isComissionCallInProgress =
      !accountsContainer?.isComissionCallInProgress
    if (
      isAccountHold ||
      isAccountIsBE ||
      checkNumberOfBuyers ||
      isComissionCallInProgress
    ) {
      this.isToShowNotificationErrorPopup = true
      this.dcsvStatus = false
    } else {
      this.showWarningPopup = true
      this.dcsvStatus = false
    }
  }

  handleKeepMeBrandAffiliate = async () => {
    const accountId =
      customerContainer?.profileResponse?.accounts?.referenceAttribute || ''
    const accountDetails = {
      dismiss: true,
      sapId: accountId,
    }
    await accountsContainer?.updateDcsvStatus(accountDetails)
    this.dcsvStatus = false
  }

  handleCloseWarningPopupModal = () => {
    this.showWarningPopup = false
  }

  handleRedirectToDownConvert = history => {
    if (this.isPhoneNumberVerified) {
      this.showWarningPopup = false
      this.dcsvStatus = false
      accountsContainer.selectedAccountType = ACCOUNT_TYPE_MEMBER
      history.push(`${pageNames.b2bAccountProfile}`, { state: true })
    } else {
      this.dcsvStatus = false
      this.showWarningPopup = false
      customerContainer.fromSessionVerifyPhonePopup = true
      this.isFromFourMonthNotification = true
    }
  }

  handleCloseNotificationErrorPopup = () => {
    this.isToShowNotificationErrorPopup = false
  }

  handleSignOutClick = async () => {
    const activeLocale = getLocaleCodeFromUrl({
      defaultLocale: 'en_US',
      isReverseType: true,
    })
    const localeInUrl = activeLocale?.toLowerCase()?.split('_')
    const currentUrlLocale = `${localeInUrl[1]}/${localeInUrl[0]}`
    await customerContainer.logout()
    if (IS_BROWSER) {
      window.location.reload()
      const newUrl = `${window.location.origin}/${currentUrlLocale}`
      window.location.href = newUrl
    }
  }

  handleSwitchToMember = () => {
    this.isFromFourMonthNotification = false
    this.isFromFourMonthCall = false
    customerContainer.isAccountTerminated = false
    customerContainer.fromSessionVerifyPhonePopup = true
    this.verifyPhoneBasedOnReEnroll = true
    this.invitationType = this.isReEnrollAsMember
    setLocalStorage('phoneVerifyFromSession', true)
    this.phoneVerifyFromSession = true
    this.isToShowReEnrollPop = false
  }

  handleReEnrollAsBrandAffiliate = () => {
    this.isFromFourMonthNotification = false
    this.isFromFourMonthCall = false
    customerContainer.isAccountTerminated = false
    customerContainer.fromSessionVerifyPhonePopup = true
    this.verifyPhoneBasedOnReEnroll = true
    this.invitationType = this.isReEnrollASBrandAffiliate
    setLocalStorage('phoneVerifyFromSession', true)
    this.phoneVerifyFromSession = true
    this.isToShowReEnrollPop = false
  }

  handleSignOut = () => {
    this.modalIgnored = false
    this.hasCheckedOnProfileLoad = false
    this.showRegionRestrictionModal = false
    this.handleSignOutClick()
    customerContainer.isAccountTerminated = false
    setLocalStorage('isToShowReEnrollSignOutErrorPopup', true)
  }

  handleNeverMindSignOut = () => {
    this.modalIgnored = false
    this.hasCheckedOnProfileLoad = false
    this.showRegionRestrictionModal = false
    this.handleSignOutClick()
    customerContainer.fromSessionVerifyPhonePopup = false
    setLocalStorage('isToShowReEnrollSignOutErrorPopup', true)
  }

  handleClose = () => {
    setLocalStorage('isToShowReEnrollSignOutErrorPopup', false)
    this.isToShowReEnrollSignOutErrorPopup = false
  }

  handleReEnrollCheckCodeVerified = () => {
    this.dcsvStatus = false
    customerContainer.isAccountTerminated = false
    jitsuSignUpRedirect({ reEnroll: true, type: this.invitationType })
  }

  handleNeverMindDoItLater = () => {
    customerContainer.fromSessionVerifyPhonePopup = false
  }

  getMessage = () => {
    const accountType =
      customerContainer?.profileResponse?.accounts?.accountType || ''
    const messages = []

    const enableFourMonthNotification = convertToBoolean(
      APPConfig?.getAppConfig()?.enableFourMonthNotification
    )

    if (
      (accountType === ACCOUNT_TYPE_BRAND_AFFILIATE ||
        accountsContainer.isAccountIsBE) &&
      enableFourMonthNotification
    ) {
      if (!accountsContainer?.isComissionCallInProgress) {
        messages.push(
          i18nTranslate(
            'ba_to_member_feature_not_available',
            'This feature is not currently available due to the commission run in progress. Please check back later.'
          )
        )
      } else {
        if (
          accountsContainer.isAccountIsBE ||
          accountsContainer.checkNumberOfBuyers
        ) {
          messages.push(
            i18nTranslate(
              'ba_to_member_account_is_not_be_error',
              'Business Entity and Joint Brand Affiliate accounts are not eligible to change to a Member account online. Please contact distributor support or your account manager for more information.'
            )
          )
        }
        if (accountsContainer.isAccountHold) {
          messages.push(
            i18nTranslate(
              'ba_to_member_account_hold_error',
              'Your account has a hold and is not eligible to change to a Member account type online. Please contact distributor support or your account manager for more information.'
            )
          )
        }
      }
    }
    return messages
  }
}

const globalModalsContainer = new GlobalModalsContainer()

export { globalModalsContainer, GlobalModalsContainer }
export default globalModalsContainer
